/**
 * Trade CSV Parser
 *
 * Supports three CSV formats:
 *
 * 1. Notion Readiness Tracker exports:
 *    Has Win/Loss checkbox columns + R/Factor directly. No dollar P/L.
 *
 * 2. TradeZella exports (from Tradovate accounts):
 *    Columns include: Net P&L, Gross P&L, Status (Win/Loss), Side, Quantity, etc.
 *
 * 3. Raw Tradovate / Generic trade CSVs:
 *    Any CSV with a pnl, P/L, PnL, Profit/Loss, or similar column.
 *
 * Extracts: totalTrades, winCount, avgWinner (R), avgLoser (R), per-trade details
 */

export interface ParsedTrade {
  pnl: number;
  isWin: boolean;
  rFactor: number; // R-multiple (computed after parsing, using avgLoser as 1R)
  symbol?: string;
  side?: string;
  quantity?: number;
  date?: string;
}

export interface ParsedStats {
  totalTrades: number;
  winCount: number;
  avgWinnerDollar: number;
  avgLoserDollar: number;
  trades: ParsedTrade[];
}

export type CSVParseResult =
  | { success: true; stats: ParsedStats }
  | { success: false; error: string };

/**
 * Parse a CSV string into rows of string arrays (handles quoted commas, strips BOM)
 */
function parseCSVRows(text: string): string[][] {
  // Strip UTF-8 BOM if present
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
  const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
  return lines.map(line => {
    const row: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        row.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    row.push(current.trim());
    return row;
  });
}

/**
 * Clean a dollar/number string: remove $, parentheses (negative), commas
 */
function parseDollarValue(val: string): number {
  let cleaned = val.replace(/[$,\s]/g, '');
  if (cleaned.startsWith('(') && cleaned.endsWith(')')) {
    cleaned = '-' + cleaned.slice(1, -1);
  }
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

/**
 * Find a column index by trying multiple header names (case-insensitive)
 */
function findColumn(headers: string[], patterns: RegExp[]): number {
  for (const pattern of patterns) {
    const idx = headers.findIndex(h => pattern.test(h.trim()));
    if (idx !== -1) return idx;
  }
  return -1;
}

/**
 * Parse a Tradovate / TradeZella CSV and extract trading stats
 */
export function parseTradovateCSV(csvText: string): CSVParseResult {
  try {
    const rows = parseCSVRows(csvText);

    if (rows.length < 2) {
      return { success: false, error: 'CSV file appears empty or has no data rows.' };
    }

    const headers = rows[0];
    const dataRows = rows.slice(1);

    // --- Detect format ---

    // Check for Notion Readiness Tracker format (Win/Loss checkboxes + R/Factor)
    const winCol = findColumn(headers, [/^win$/i]);
    const lossCol = findColumn(headers, [/^loss$/i]);
    const rFactorCol = findColumn(headers, [/^r\/factor$/i, /^r.factor$/i, /^rfactor$/i, /^r_factor$/i]);

    if (winCol !== -1 && lossCol !== -1 && rFactorCol !== -1) {
      // --- Notion format: Win/Loss + R/Factor directly ---
      return parseNotionFormat(dataRows, headers, winCol, lossCol, rFactorCol);
    }

    // Try TradeZella format (has "Net P&L" and "Status" columns)
    const netPnlCol = findColumn(headers, [/^net\s*p[&\/]l$/i, /^net\s*pnl$/i]);
    const grossPnlCol = findColumn(headers, [/^gross\s*p[&\/]l$/i, /^gross\s*pnl$/i]);
    const statusCol = findColumn(headers, [/^status$/i]);

    // Use Net P&L if available, otherwise Gross P&L, otherwise generic search
    let pnlCol = netPnlCol !== -1 ? netPnlCol : grossPnlCol;

    if (pnlCol === -1) {
      // Fallback: generic P/L column search (includes raw Tradovate "pnl")
      pnlCol = findColumn(headers, [
        /^pnl$/i,
        /^p[\/&]l$/i,
        /^profit[\/\s]?loss$/i,
        /^realized\s*p[\/&]?l$/i,
        /^trade\s*p[\/&]?l$/i,
        /^net\s*profit$/i,
        /^profit$/i,
      ]);
    }

    if (pnlCol === -1) {
      return {
        success: false,
        error: `Could not find a P/L column. Found headers: ${headers.slice(0, 10).join(', ')}. Expected "Net P&L", "Gross P&L", "P/L", "PnL", "R/Factor", or similar.`,
      };
    }

    // --- Dollar P/L format (Tradovate / TradeZella) ---
    return parseDollarPnLFormat(dataRows, headers, pnlCol, statusCol);
  } catch (err) {
    return {
      success: false,
      error: `Failed to parse CSV: ${err instanceof Error ? err.message : 'Unknown error'}`,
    };
  }
}

/**
 * Parse Notion Readiness Tracker format (Win/Loss checkboxes + R/Factor)
 */
function parseNotionFormat(
  dataRows: string[][],
  headers: string[],
  winCol: number,
  lossCol: number,
  rFactorCol: number,
): CSVParseResult {
  const symbolCol = findColumn(headers, [/^pair$/i, /^symbol$/i, /^instrument$/i]);
  const sideCol = findColumn(headers, [/^position$/i, /^side$/i]);
  const dateCol = findColumn(headers, [/^date$/i]);

  const allTrades: ParsedTrade[] = [];

  for (const row of dataRows) {
    if (row.length <= rFactorCol) continue;

    const win = row[winCol]?.trim().toLowerCase();
    const loss = row[lossCol]?.trim().toLowerCase();

    // Skip rows that aren't win or loss
    if (win !== 'yes' && loss !== 'yes') continue;

    const isWin = win === 'yes';
    const rFactor = parseFloat(row[rFactorCol]) || 0;

    const trade: ParsedTrade = {
      pnl: 0, // No dollar P/L in Notion format
      isWin,
      rFactor: isWin ? rFactor : -Math.abs(rFactor),
    };

    if (symbolCol !== -1 && row.length > symbolCol) trade.symbol = row[symbolCol].trim();
    if (sideCol !== -1 && row.length > sideCol) trade.side = row[sideCol].trim();
    if (dateCol !== -1 && row.length > dateCol) trade.date = row[dateCol].trim();

    allTrades.push(trade);
  }

  if (allTrades.length === 0) {
    return { success: false, error: 'No valid trades found in the CSV.' };
  }

  const winners = allTrades.filter(t => t.isWin);
  const losers = allTrades.filter(t => !t.isWin);

  const avgWinnerR = winners.length > 0
    ? winners.reduce((a, b) => a + b.rFactor, 0) / winners.length
    : 0;

  const avgLoserR = losers.length > 0
    ? Math.abs(losers.reduce((a, b) => a + b.rFactor, 0) / losers.length)
    : 0;

  return {
    success: true,
    stats: {
      totalTrades: allTrades.length,
      winCount: winners.length,
      // For Notion format, dollar values hold R-multiples directly
      avgWinnerDollar: Math.round(avgWinnerR * 100) / 100,
      avgLoserDollar: Math.round(avgLoserR * 100) / 100,
      trades: allTrades,
    },
  };
}

/**
 * Parse dollar P/L format (Tradovate / TradeZella)
 */
function parseDollarPnLFormat(
  dataRows: string[][],
  headers: string[],
  pnlCol: number,
  statusCol: number,
): CSVParseResult {
  const symbolCol = findColumn(headers, [/^symbol$/i, /^instrument$/i, /^ticker$/i]);
  const sideCol = findColumn(headers, [/^side$/i, /^action$/i, /^type$/i]);
  const qtyCol = findColumn(headers, [/^qty$/i, /^quantity$/i, /^size$/i]);
  const dateCol = findColumn(headers, [
    /^close\s*date$/i, /^sold\s*timestamp$/i, /^exit\s*date$/i,
    /^date$/i, /^timestamp$/i, /^bought\s*timestamp$/i, /^open\s*date$/i,
  ]);

  const allTrades: ParsedTrade[] = [];

  for (const row of dataRows) {
    if (row.length <= pnlCol) continue;

    const pnl = parseDollarValue(row[pnlCol]);
    let isWin: boolean | null = null;

    if (statusCol !== -1 && row.length > statusCol) {
      const status = row[statusCol].trim().toLowerCase();
      if (status === 'win') isWin = true;
      else if (status === 'loss') isWin = false;
      else continue;
    } else {
      if (pnl > 0) isWin = true;
      else if (pnl < 0) isWin = false;
      else continue;
    }

    const trade: ParsedTrade = {
      pnl,
      isWin: isWin!,
      rFactor: 0,
    };

    if (symbolCol !== -1 && row.length > symbolCol) trade.symbol = row[symbolCol].trim();
    if (sideCol !== -1 && row.length > sideCol) trade.side = row[sideCol].trim();
    if (qtyCol !== -1 && row.length > qtyCol) trade.quantity = parseFloat(row[qtyCol]) || undefined;
    if (dateCol !== -1 && row.length > dateCol) trade.date = row[dateCol].trim();

    allTrades.push(trade);
  }

  if (allTrades.length === 0) {
    return { success: false, error: 'No valid trades found in the CSV.' };
  }

  const winners = allTrades.filter(t => t.isWin);
  const losers = allTrades.filter(t => !t.isWin);

  const avgWinnerDollar = winners.length > 0
    ? winners.reduce((a, b) => a + b.pnl, 0) / winners.length
    : 0;

  const avgLoserDollar = losers.length > 0
    ? losers.reduce((a, b) => a + Math.abs(b.pnl), 0) / losers.length
    : 0;

  // Compute R-factor per trade (using avgLoser as 1R baseline)
  const oneR = avgLoserDollar > 0 ? avgLoserDollar : 1;
  for (const trade of allTrades) {
    trade.rFactor = Math.round((trade.pnl / oneR) * 100) / 100;
  }

  return {
    success: true,
    stats: {
      totalTrades: allTrades.length,
      winCount: winners.length,
      avgWinnerDollar: Math.round(avgWinnerDollar * 100) / 100,
      avgLoserDollar: Math.round(avgLoserDollar * 100) / 100,
      trades: allTrades,
    },
  };
}
